/**
 * useTaskAutomation — Auto-creates tasks based on client-side triggers
 *
 * Runs when AM opens the dashboard. Evaluates triggers and creates tasks
 * if no pending task exists for the same trigger (dedup by source + source_ref).
 *
 * Triggers:
 * 1. Health drop → client at_risk or critical
 * 2. SLA breach → unresponded message past threshold
 * 3. Contract expiring → < 7 days remaining with pending leads
 * 4. Low engagement → contact_rate < 20%
 */

import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { AMTask, TaskSource } from "@/hooks/useAMTasks";
import type { ClientSLAInfo } from "@/hooks/useSLAMonitor";

interface BiweeklyContract {
  agentId: string;
  agentName: string;
  daysRemaining: number;
  contractId: string;
}

interface AutomationInput {
  amId: string | null;
  tasks: AMTask[];
  /** Client health data: agentId → { status, agentName, isNew (grace period) } */
  clientHealth: Map<string, { status: string; name: string; isNew?: boolean }>;
  /** SLA statuses from useSLAMonitor */
  slaStatuses: ClientSLAInfo[];
  /** Contracts nearing expiry: { agentId, agentName, daysRemaining } */
  expiringContracts: { agentId: string; agentName: string; daysRemaining: number }[];
  /** Biweekly contracts nearing cycle end (< 4 days remaining) for upsell tasks */
  biweeklyContracts?: BiweeklyContract[];
  /** Enabled flag — set to true only after all data is loaded */
  enabled: boolean;
}

async function createAutoTask(
  amId: string,
  agentId: string,
  title: string,
  source: TaskSource,
  sourceRef: string,
  priority: "medium" | "high" | "urgent",
  dueDate?: string
) {
  const { error } = await supabase.from("am_tasks").insert({
    am_id: amId,
    agent_id: agentId,
    title,
    source,
    source_ref: sourceRef,
    priority,
    due_date: dueDate || null,
  });

  if (error) {
    console.error("[TaskAutomation] Failed to create auto-task:", error);
  }
}

function hasPendingTask(tasks: AMTask[], source: TaskSource, sourceRef: string): boolean {
  return tasks.some(
    (t) => t.source === source && t.sourceRef === sourceRef && t.status !== "done"
  );
}

function todayPlusDays(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
}

export function useTaskAutomation({
  amId,
  tasks,
  clientHealth,
  slaStatuses,
  expiringContracts,
  biweeklyContracts,
  enabled,
}: AutomationInput) {
  const lastFingerprint = useRef<string>("");
  const running = useRef(false);

  // Build a fingerprint of the trigger data so we only re-evaluate on real changes
  const fingerprint = enabled
    ? JSON.stringify({
        health: Array.from(clientHealth.entries()).map(([id, h]) => `${id}:${h.status}:${h.isNew}`),
        sla: slaStatuses.map((s) => `${s.agentId}:${s.status}:${s.messageId}`),
        contracts: expiringContracts.map((c) => `${c.agentId}:${c.daysRemaining}`),
        biweekly: (biweeklyContracts ?? []).map((c) => `${c.agentId}:${c.daysRemaining}`),
      })
    : "";

  useEffect(() => {
    if (!enabled || !amId || running.current) return;
    if (fingerprint === lastFingerprint.current) return;
    lastFingerprint.current = fingerprint;
    running.current = true;

    const run = async () => {
      let created = 0;

      // 1. Health drop → at_risk or critical (skip new clients in grace period)
      for (const [agentId, info] of clientHealth) {
        if ((info.status === "at_risk" || info.status === "critical") && !info.isNew) {
          const ref = `health_${agentId}`;
          if (!hasPendingTask(tasks, "auto_health", ref)) {
            const priority = info.status === "critical" ? "urgent" : "high";
            await createAutoTask(
              amId,
              agentId,
              `Review ${info.name} — health ${info.status.replace("_", " ")}`,
              "auto_health",
              ref,
              priority,
              todayPlusDays(info.status === "critical" ? 1 : 3)
            );
            created++;
          }
        }
      }

      // 2. SLA breach → create task for breached clients
      for (const sla of slaStatuses) {
        if (sla.status === "breach" && sla.messageId) {
          const ref = `sla_${sla.messageId}`;
          if (!hasPendingTask(tasks, "auto_sla", ref)) {
            await createAutoTask(
              amId,
              sla.agentId,
              `Respond to ${sla.agentName || "client"} — SLA breached (${sla.elapsedHours}h)`,
              "auto_sla",
              ref,
              "urgent",
              todayPlusDays(0) // Due today
            );
            created++;
          }
        }
      }

      // 3. Contract expiring (< 7 days)
      for (const contract of expiringContracts) {
        const ref = `contract_${contract.agentId}`;
        if (!hasPendingTask(tasks, "auto_contract", ref)) {
          await createAutoTask(
            amId,
            contract.agentId,
            `Review expiring contract for ${contract.agentName} (${contract.daysRemaining}d left)`,
            "auto_contract",
            ref,
            "high",
            todayPlusDays(Math.min(contract.daysRemaining, 3))
          );
          created++;
        }
      }

      // 4. Biweekly upsell — create task 3 days before cycle ends
      for (const bw of biweeklyContracts ?? []) {
        if (bw.daysRemaining <= 3) {
          const ref = `biweekly_upsell_${bw.contractId}`;
          if (!hasPendingTask(tasks, "auto_contract", ref)) {
            await createAutoTask(
              amId,
              bw.agentId,
              `Biweekly ending for ${bw.agentName} (${bw.daysRemaining}d left) — schedule upsell to monthly`,
              "auto_contract",
              ref,
              "high",
              todayPlusDays(Math.min(bw.daysRemaining, 2))
            );
            created++;
          }
        }
      }

      if (created > 0) {
        console.log(`[TaskAutomation] Created ${created} auto-tasks`);
      }
    };

    run().finally(() => {
      running.current = false;
    });
  }, [enabled, amId, fingerprint, tasks]);
}
